import * as React from "react";

/**
 * Buzerlístok — vlastná sada mood ikon (5 stavov).
 * Štýl: Lucide konvencie — 24x24 viewBox, stroke = currentColor, width 2, round cap/join.
 * Zdedia farbu z rodiča: čierne v light, biele v dark, coral v selected stave.
 *
 * Pridaj do web/src/ (alebo zlúč do icons.tsx) a použi <MoodGreat size={28} /> atď.
 */

export type MoodKey = "great" | "good" | "neutral" | "wavy" | "hard";

type MoodIconProps = {
  size?: number;
  strokeWidth?: number;
  className?: string;
  title?: string;
};

function Face({
  size = 24,
  strokeWidth = 2,
  className,
  title,
  children,
}: MoodIconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

// Skvelý — usmiate (privreté) oči + široký úsmev
export function MoodGreat(props: MoodIconProps) {
  return (
    <Face {...props}>
      <path d="M7.5 10.5 Q9 8.9 10.5 10.5" />
      <path d="M13.5 10.5 Q15 8.9 16.5 10.5" />
      <path d="M7.4 13.8 Q12 18.2 16.6 13.8" />
    </Face>
  );
}

// V pohode — bodové oči + jemný úsmev
export function MoodGood(props: MoodIconProps) {
  return (
    <Face {...props}>
      <path d="M9 10 h0.01" />
      <path d="M15 10 h0.01" />
      <path d="M8.5 14.3 Q12 16.6 15.5 14.3" />
    </Face>
  );
}

// Neutrálne — bodové oči + rovná čiara
export function MoodNeutral(props: MoodIconProps) {
  return (
    <Face {...props}>
      <path d="M9 10 h0.01" />
      <path d="M15 10 h0.01" />
      <path d="M9 15 H15" />
    </Face>
  );
}

// Výkyvy — bodové oči + vlnka
export function MoodWavy(props: MoodIconProps) {
  return (
    <Face {...props}>
      <path d="M9 10 h0.01" />
      <path d="M15 10 h0.01" />
      <path d="M8 15 Q10 13.4 12 15 T16 15" />
    </Face>
  );
}

// Ťažký — starostlivé obočie + bodové oči + frown
export function MoodHard(props: MoodIconProps) {
  return (
    <Face {...props}>
      <path d="M7.3 9.4 L10 8.6" />
      <path d="M16.7 9.4 L14 8.6" />
      <path d="M9 10.8 h0.01" />
      <path d="M15 10.8 h0.01" />
      <path d="M8 16.2 Q12 13.2 16 16.2" />
    </Face>
  );
}

/**
 * Poradie + slovenské labely na rovno do selektora.
 * Príklad:
 *   {MOODS.map(m => <button key={m.key}><m.Icon size={28} /><span>{m.label}</span></button>)}
 */
export const MOODS: { key: MoodKey; label: string; Icon: React.FC<MoodIconProps> }[] = [
  { key: "great", label: "Skvelý", Icon: MoodGreat },
  { key: "good", label: "V pohode", Icon: MoodGood },
  { key: "neutral", label: "Neutrálne", Icon: MoodNeutral },
  { key: "wavy", label: "Výkyvy", Icon: MoodWavy },
  { key: "hard", label: "Ťažký", Icon: MoodHard },
];

export const MOOD_ICON: Record<MoodKey, React.FC<MoodIconProps>> = {
  great: MoodGreat,
  good: MoodGood,
  neutral: MoodNeutral,
  wavy: MoodWavy,
  hard: MoodHard,
};
